import React from 'react'
import { Modal, List, Badge, Tag, Button, Divider, Space, Typography } from 'antd'
import { CalendarOutlined, ClockCircleOutlined, UserOutlined, FlagOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import type { Dayjs } from 'dayjs'
import type { PlanogramPlan } from '../../../types/plan'

const { Title, Text } = Typography

interface MilestoneModalProps {
  visible: boolean
  onClose: () => void
  date: Dayjs | null
  plans: PlanogramPlan[]
  onPlanClick: (plan: PlanogramPlan) => void
}

const MilestoneModal: React.FC<MilestoneModalProps> = ({
  visible,
  onClose,
  date,
  plans,
  onPlanClick
}) => {
  const { t } = useTranslation();
  
  // Get status color
  const getStatusColor = (status: string): string => {
    const colorMap: Record<string, string> = {
      'Pending': '#d9d9d9',
      'InProgress': '#1890ff',
      'Completed': '#52c41a',
      'Paused': '#fa8c16',
      'Cancelled': '#ff4d4f'
    }
    return colorMap[status] || '#d9d9d9'
  }

  // Get status text
  const getStatusText = (status: string): string => {
    return t(`planMilestone.statuses.${status}`) || status
  }

  // Get plan type badge color
  const getPlanTypeBadge = (planType: string): string => {
    const badgeMap: Record<string, string> = {
      'Major': 'red',
      'Regular': 'blue',
      'Minor': 'green',
      'Urgent': 'orange',
      'Special': 'purple'
    }
    return badgeMap[planType] || 'default'
  }

  // Get plan type text
  const getPlanTypeText = (planType: string): string => {
    return t(`planMilestone.planTypes.${planType}`) || planType
  }

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <CalendarOutlined style={{ color: '#1890ff' }} />
          <span>{t('planMilestone.milestone.modalTitle', { date: date?.format('YYYY/MM/DD') })}</span>
          <Badge count={plans.length} style={{ backgroundColor: '#1890ff' }} />
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={600}
      style={{ top: 20 }}
    >
      {plans.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
          <CalendarOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
          <div>{t('planMilestone.milestone.noMilestones')}</div>
        </div>
      ) : (
        <List
          dataSource={plans}
          renderItem={(plan) => (
            <List.Item
              style={{
                padding: '16px 0',
                borderBottom: '1px solid #f0f0f0'
              }}
              actions={[
                <Button
                  type="primary"
                  size="small"
                  onClick={() => onPlanClick(plan)}
                >
                  {t('planMilestone.milestone.viewDetails')}
                </Button>
              ]}
            >
              <List.Item.Meta
                avatar={
                  <div
                    style={{
                      width: '8px',
                      height: '60px',
                      backgroundColor: getStatusColor(plan.status),
                      borderRadius: '4px'
                    }}
                  />
                }
                title={
                  <div style={{ marginBottom: '8px' }}>
                    <Space>
                      <Text strong style={{ fontSize: '16px' }}>
                        {plan.planName}
                      </Text>
                      <Tag color={getPlanTypeBadge(plan.planType)}>
                        {getPlanTypeText(plan.planType)}
                      </Tag>
                      <Tag color={getStatusColor(plan.status)}>
                        {getStatusText(plan.status)}
                      </Tag>
                    </Space>
                  </div>
                }
                description={
                  <div>
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FlagOutlined style={{ color: '#666' }} />
                        <Text type="secondary">{t('planMilestone.planCode')}: {plan.planCode}</Text>
                      </div>
                      
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <ClockCircleOutlined style={{ color: '#666' }} />
                        <Text type="secondary">{t('planMilestone.currentMilestone')}: {plan.currentMilestone}</Text>
                      </div>
                      
                      {plan.projectOwner && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <UserOutlined style={{ color: '#666' }} />
                          <Text type="secondary">{t('planMilestone.projectOwner')}: {plan.projectOwner}</Text>
                        </div>
                      )}
                      
                      {plan.templateReference && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Text type="secondary">{t('planMilestone.templateReference')}: {plan.templateReference}</Text>
                        </div>
                      )}
                    </Space>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      )}
    </Modal>
  )
}

export default MilestoneModal